import React, { PropsWithChildren } from 'react'
import { PageSettings } from '@http/api-models/page-settings'
import { PageLayout } from '@shared/components/page-layout'
import { PageLayoutOwnProps } from '@shared/components/page-layout/model'
import { NextPageFC } from '@shared/models/next-fc'
import { parse } from 'cookie'
import { GetServerSideProps } from 'next'

import { AdminRootPage } from '../admin/components/RootPage'
import { AdminNotFoundPage } from '../admin/errors'
import { getPageConfigByUrl } from '../admin/http'
import { getHeadPropsFromPageMeta } from '../admin/utils'
import { regions } from '../regions/list'
import { regionalPages } from '../regions/regional-pages'
import {
	getDynamicRegionalPage,
	getPageParamOrDefault,
} from '../regions/runtime-components/helpers'
import { RuntimeComponentProps } from '../regions/runtime-components/types'
import {
	getAppliedCityFromCookies,
	notFoundSSP,
	serverSideRegionRedirectFactory,
} from '../regions/utils'
import { NotFoundScreen } from '../screens/not-found'

const Resolver: NextPageFC<
	RuntimeComponentProps & { pageSettings?: PageSettings },
	PageLayoutOwnProps
> = ({ regionalPagePath, pageSettings }, ...rest) => {
	const Component = React.useMemo(() => {
		const RegionalPage = regionalPagePath ? getDynamicRegionalPage(regionalPagePath) : null
		return RegionalPage ? RegionalPage.DynamicComponent : NotFoundScreen
	}, [regionalPagePath])

	/**
	 * Интеграция админки
	 */
	if (pageSettings?.admin?.page) {
		return <AdminRootPage {...pageSettings} />
	}

	return <Component {...(rest as any)} />
}

Resolver.layout = (props) => {
	return getPageParamOrDefault(
		{ ...props.pageProps, children: (props as PropsWithChildren<typeof props>).children },
		'layout',
		PageLayout
	)
}

Resolver.headProps = (props) => {
	/**
	 * Интеграция админки
	 */
	if (props.pageSettings?.admin?.page) {
		return getHeadPropsFromPageMeta(props.pageSettings?.admin?.page.pageMeta)
	}

	return getPageParamOrDefault(props as any, 'headProps', {
		title: 'Запрашиваемый раздел не существует',
	})
}

Resolver.breadcrumb = (props) => {
	/**
	 * Интеграция админки
	 */
	if (!props.pageSettings?.admin?.page?.breadcrumbTitle) return { title: null }

	return {
		title: props.pageSettings.admin.page.breadcrumbTitle,
	}
}

export const getServerSideProps: GetServerSideProps<
	RuntimeComponentProps & PageSettings,
	{ rest: string[] }
> = async (context) => {
	const path = context.params?.rest.join('/') ?? ''
	/**
	 * Оставляем обратную совместимость на глубокие маршруты которые переехали
	 * на региональные страницы. Например /personal/invest/
	 * будет интерпретироваться как /{contextCity}/personal/invest/ но если пользователь не
	 * выбрал город мы будем отдавать по томе жу пути страницу
	 */
	const appliedCityFromCookies = getAppliedCityFromCookies(parse(context.req.headers?.cookie || ''))

	if (regionalPages.includes(path)) {
		if (appliedCityFromCookies) {
			return serverSideRegionRedirectFactory(appliedCityFromCookies, context.resolvedUrl, false)
		}

		return {
			props: {
				city: appliedCityFromCookies,
				regionalPagePath: path,
			},
		}
	}

	/**
	 * Интеграция админки
	 */
	try {
		const regionCandidate = path.slice(0, path.indexOf('/'))
		const hasRegionInPath = Boolean(regions[regionCandidate])
		const pageAdminUrl = hasRegionInPath ? path.slice(path.indexOf('/')) : `/${path}`
		const pageConfig = await getPageConfigByUrl(pageAdminUrl)

		if (!pageConfig.isPublished) {
			throw new AdminNotFoundPage()
		}

		const isRegionalPage = pageConfig.isRegional

		if (!isRegionalPage && hasRegionInPath) {
			return notFoundSSP(context)
		}

		if (isRegionalPage && appliedCityFromCookies && !hasRegionInPath) {
			return serverSideRegionRedirectFactory(appliedCityFromCookies, pageAdminUrl, false)
		}

		return {
			props: {
				city: hasRegionInPath ? regionCandidate : null,
				pageSettings: {
					region: {
						contextCity: hasRegionInPath ? regionCandidate : null,
						enableChallenge: appliedCityFromCookies !== regionCandidate || !hasRegionInPath,
					},
					admin: {
						page: pageConfig,
					},
				},
			},
		}
	} catch (err: any) {
		if (err instanceof AdminNotFoundPage || (err.isAxiosError && err.response?.status === 404)) {
			return notFoundSSP(context)
		}

		throw err
	}

	return notFoundSSP(context)
}

export default Resolver
